import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { History, Undo2, Upload, Trophy, Users, ListChecks, AlertTriangle } from "lucide-react";

const TYPE_ICON = { tournament: Trophy, league: Users, season: ListChecks };

export default function AdminImportHistory() {
  const [batches, setBatches] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/live/imports").then(({ data }) => setBatches(data)).catch(() => setBatches([]));
  useEffect(() => { load(); }, []);

  const undo = () => {
    setBusy(true);
    api.post(`/live/imports/${confirm.import_id}/undo`)
      .then(({ data }) => { toast.success(`Undone — ${data.games_removed} games removed, ${data.teams_deleted} teams deleted`); setConfirm(null); load(); })
      .catch((e) => toast.error(e?.response?.data?.detail || "Undo failed")).finally(() => setBusy(false));
  };

  return (
    <AdminLayout>
    <div className="max-w-5xl mx-auto p-6" data-testid="admin-import-history">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="label-overline text-[#E63946] flex items-center gap-2"><History className="w-4 h-4" /> CBR Live</div>
          <h1 className="font-display text-3xl uppercase tracking-tight text-[#0A192F]">Import History</h1>
        </div>
        <Link to="/admin/live/import" data-testid="new-import-link" className="flex items-center gap-2 bg-[#E63946] text-white text-sm font-semibold uppercase tracking-wider px-4 py-2 rounded-sm hover:bg-[#c62f3b]"><Upload className="w-4 h-4" /> New Import</Link>
      </div>

      {!batches ? <div className="py-16 text-center text-slate-400">Loading…</div>
        : batches.length === 0 ? <div className="bg-white border border-dashed border-slate-200 rounded-lg py-16 text-center text-slate-400">No imports yet.</div>
        : (
          <div className="space-y-2" data-testid="import-list">
            {batches.map((b) => {
              const Icon = TYPE_ICON[b.import_type] || ListChecks;
              const undone = b.status === "undone";
              return (
                <div key={b.import_id} data-testid={`import-row-${b.import_id}`} className={`bg-white border rounded-lg p-4 ${undone ? "border-slate-100 opacity-60" : "border-slate-200"}`}>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-sm bg-[#0A192F] grid place-items-center shrink-0"><Icon className="w-4 h-4 text-[#E63946]" /></div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-[#0A192F] capitalize">{b.import_type}</span>
                        {b.file_name && <span className="text-xs text-slate-400 truncate">· {b.file_name}</span>}
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${undone ? "bg-slate-100 text-slate-400" : "bg-emerald-100 text-emerald-700"}`}>{undone ? "Undone" : "Active"}</span>
                      </div>
                      <div className="text-[11px] text-slate-400 mt-0.5">{new Date(b.created_at).toLocaleString()} · by {b.imported_by?.name || "—"}</div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-[11px] text-slate-500">
                        <Stat label="Games" v={b.games_imported} />
                        <Stat label="Teams +" v={b.teams_created} />
                        <Stat label="Updated" v={b.teams_updated} />
                        <Stat label="Dups skipped" v={b.duplicates_skipped} />
                        <Stat label="Errors" v={b.errors} />
                        <Stat label="Ratings" v={b.ratings_updated} />
                        <Stat label="Time" v={`${b.time_seconds}s`} />
                      </div>
                      {b.tournament_id && !undone && <Link to={`/live/tournaments/${b.tournament_id}`} className="text-xs font-semibold text-[#E63946] mt-1 inline-block">View tournament page →</Link>}
                    </div>
                    {!undone && (
                      <button data-testid={`undo-import-${b.import_id}`} onClick={() => setConfirm(b)}
                        className="flex items-center gap-1.5 text-xs font-semibold text-[#E63946] border border-[#E63946]/40 hover:bg-[#E63946] hover:text-white px-3 py-2 rounded-sm transition-colors shrink-0">
                        <Undo2 className="w-3.5 h-3.5" /> Undo
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

      {confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="undo-confirm-modal">
          <div className="absolute inset-0 bg-black/50" onClick={() => setConfirm(null)} />
          <div className="relative bg-white w-full max-w-md rounded-lg p-6">
            <div className="flex items-center gap-2 text-[#E63946] mb-2"><AlertTriangle className="w-5 h-5" /><h3 className="font-display text-xl uppercase tracking-tight">Undo Import?</h3></div>
            <p className="text-sm text-slate-600">This removes <b>{confirm.games_imported} games</b> from this import and recomputes all affected team records, stats, Strength of Schedule, CBR Team Rating™ and rankings. Champion accomplishments &amp; unposted social drafts from this import are removed. Teams are deleted only if this import created them and they have no other games, roster or followers.</p>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setConfirm(null)} className="flex-1 border border-slate-200 text-slate-600 text-sm font-semibold uppercase tracking-wider py-2.5 rounded-sm">Cancel</button>
              <button data-testid="confirm-undo-btn" disabled={busy} onClick={undo} className="flex-1 bg-[#E63946] text-white text-sm font-semibold uppercase tracking-wider py-2.5 rounded-sm disabled:opacity-50">Undo Import</button>
            </div>
          </div>
        </div>
      )}
    </div>
    </AdminLayout>
  );
}
const Stat = ({ label, v }) => <span><span className="font-data text-[#0A192F]">{v}</span> {label}</span>;
